import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCookies } from 'react-cookie';
import { buildAdminFeatureConfigs } from "./adminFeatureConfig";
import {
  filterUsersBySearch,
  formatBalance,
  formatRate,
  getReturnRateValue,
} from "./adminPageListUtils";

const ADMIN_USER_TABLE_MOBILE_MAX_PX = 900;

export default function AdminPage() {
  const AUTO_REFRESH_INTERVAL_MS = 3000;
  const userTableHostRef = useRef(null);
  const userTableScrollRef = useRef(null);
  const [rowIdentityPeeks, setRowIdentityPeeks] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); // 검색어 상태
  const [balanceByUid, setBalanceByUid] = useState({});
  const [currentBalanceByUid, setCurrentBalanceByUid] = useState({});
  const [balanceLoadingByUid, setBalanceLoadingByUid] = useState({});
  const [balanceSaveLoadingByUid, setBalanceSaveLoadingByUid] = useState({});
  const [currentBalanceLoadingByUid, setCurrentBalanceLoadingByUid] = useState({});
  const [expandedBalanceByUid, setExpandedBalanceByUid] = useState({});
  const [apiUpdateLoadingByUid, setApiUpdateLoadingByUid] = useState({});
  const [manualBalanceModal, setManualBalanceModal] = useState({
    isOpen: false,
    uid: "",
    uid2: "",
    nickname: "",
    mainBalance: "",
    secondBalance: "",
  });
  const [apiChangeModal, setApiChangeModal] = useState({
    isOpen: false,
    type: "main",
    uid: "",
    nickname: "",
    api: "",
    secretkey: "",
    apiPw: "",
  });
  const [cookies, , removeCookie] = useCookies(['uuid']);

  const readUuidFromDocumentCookie = () => {
    const match = document.cookie.match(/(?:^|;\s*)uuid=([^;]*)/);
    return match ? decodeURIComponent(match[1].trim()) : '';
  };
  const [transferLoadingByUid, setTransferLoadingByUid] = useState({});
  const [futuresSpotTransferModal, setFuturesSpotTransferModal] = useState({
    isOpen: false,
    uid: "",
    uid2: "",
    nickname: "",
    account: "main",
    direction: "futures_to_spot",
    amount: "",
    balanceInfo: null,
    balanceLoading: false,
  });
  const [futuresSpotLoadingByUid, setFuturesSpotLoadingByUid] = useState({});
  const [selectedFeatureByUid, setSelectedFeatureByUid] = useState({});
  const [accountActionByUid, setAccountActionByUid] = useState({});
  const [infoActionByUid, setInfoActionByUid] = useState({});


  // 쿠키에서 UUID를 이용하여 사용자 정보 가져오기
  const[id,setID] = useState('');
  const[pw,setPW] = useState('');
  const[admin_possible,setAD_PS] = useState('');

  // const[margin,setMargin] = useState('');

  useEffect(() => {
    const uuid = cookies.uuid || readUuidFromDocumentCookie();
    if (!uuid) {
      alert('로그인 정보가 존재하지 않습니다. 관리자 로그인 페이지로 이동합니다.');
      window.location.href = `/AdminLogin`;
      return;
    }
    document.cookie = 'admin_target_uid=; path=/; max-age=0; SameSite=Lax';
    sessionStorage.removeItem('adminAccessContext');
    sessionStorage.removeItem('holdAccessContext');
    fetchUserInfo(uuid);
  }, []);

  useEffect(() => {
  }, [cookies.uuid]);

  const fetchUserInfo = (uuid) => {
    const encodedUuid = encodeURIComponent(uuid);
    fetch(`/SetPage/admin_user?uuid=${encodedUuid}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('서버 응답 실패');
        }
        return response.json();
      })
      .then((data) => {
        if (!Array.isArray(data) || data.length === 0 || !data[0]) {
          throw new Error('세션이 만료되었거나 관리자 정보를 찾을 수 없습니다.');
        }
        const user = data[0];
        setID(user.id);
        setPW(user.pw);
        setAD_PS(user.admin_possible);
      })
      .catch(() => {
        try {
          removeCookie('uuid', { path: '/' });
        } catch {
          document.cookie = 'uuid=; path=/; max-age=0';
        }
        alert('로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.');
        window.location.href = `/AdminLogin`;
      });
  };

  const fetchUsers = async ({ silent = false } = {}) => {
    try {
      const response = await fetch(`/admin/users`);
      if (!response.ok) {
        throw new Error('서버 응답 실패');
      }
      const data = await response.json();
      setUsers(data);
      setBalanceByUid(
        data.reduce((accumulator, user) => {
          const hasPass =
            user.pass_usdt !== null && user.pass_usdt !== undefined && user.pass_usdt !== "";
          const hasSavedBreakdown =
            (user.first_usdt !== null && user.first_usdt !== undefined && user.first_usdt !== "") ||
            (user.sec_usdt !== null && user.sec_usdt !== undefined && user.sec_usdt !== "");

          if (!hasSavedBreakdown && !hasPass) {
            return accumulator;
          }

          const mainBalance = Number(user.first_usdt || 0);
          const secondBalance = Number(user.sec_usdt || 0);
          const usePassUsdtOnly = Boolean(hasPass && !hasSavedBreakdown);
          const totalBalance = usePassUsdtOnly
            ? Number(user.pass_usdt)
            : Number((mainBalance + secondBalance).toFixed(3));

          accumulator[user.uid] = {
            mainBalance,
            secondBalance,
            totalBalance,
            uid2: user.uid_2,
            ...(usePassUsdtOnly ? { usePassUsdtOnly: true } : {}),
          };

          return accumulator;
        }, {})
      );
      setCurrentBalanceByUid(
        data.reduce((accumulator, user) => {
          const hasSavedCurrentBreakdown =
            (user.first_usdt_current !== null && user.first_usdt_current !== undefined && user.first_usdt_current !== "") ||
            (user.sec_usdt_current !== null && user.sec_usdt_current !== undefined && user.sec_usdt_current !== "");

          if (hasSavedCurrentBreakdown) {
            const mainBalance = Number(user.first_usdt_current || 0);
            const secondBalance = Number(user.sec_usdt_current || 0);

            accumulator[user.uid] = {
              mainBalance,
              secondBalance,
              totalBalance: Number((mainBalance + secondBalance).toFixed(3)),
              uid2: user.uid_2,
            };
          }

          return accumulator;
        }, {})
      );
    } catch (error) {
      console.error('Waiting List 가져오기 오류:', error);
      if (!silent) {
        alert(error.message || error);
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const hasActiveTask =
      Object.values(balanceLoadingByUid).some(Boolean) ||
      Object.values(balanceSaveLoadingByUid).some(Boolean) ||
      Object.values(currentBalanceLoadingByUid).some(Boolean) ||
      Object.values(apiUpdateLoadingByUid).some(Boolean) ||
      Object.values(transferLoadingByUid).some(Boolean);

    if (manualBalanceModal.isOpen || apiChangeModal.isOpen || futuresSpotTransferModal.isOpen || hasActiveTask) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      fetchUsers({ silent: true });
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [
    manualBalanceModal.isOpen,
    apiChangeModal.isOpen,
    futuresSpotTransferModal.isOpen,
    balanceLoadingByUid,
    balanceSaveLoadingByUid,
    currentBalanceLoadingByUid,
    apiUpdateLoadingByUid,
    transferLoadingByUid,
  ]);

  // useEffect(() => {
  //   // 서버에서 margin 값을 가져오는 함수
  //   const fetchMargin = async () => {
  //     try {
  //       const response = await fetch('/admin/margin');
  //       if (!response.ok) {
  //         throw new Error('서버 응답 실패');
  //       }
  //       const data = await response.json();
  //       if (data.length > 0) {
  //         setMargin(data[0].magin); // 첫 번째 데이터의 magin 값을 사용
  //       }
  //     } catch (error) {
  //       console.error('margin 값 가져오기 오류:', error);
  //       alert('데이터를 가져오는 데 문제가 발생했습니다.');
  //     }
  //   };

  //   // 컴포넌트가 마운트될 때 호출
  //   fetchMargin();
  // }, []);


    // 사용자 상태값 변경 시마다 useEffect 호출
  useEffect(() => {
    // 상태값이 변경될 때 필요한 작업 수행
    console.log('Users 상태값이 변경되었습니다:');
  }, [users]);


  const handleApprove = async (uid, nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님을 사용승인 처리하시겠습니까?`);

    if (confirmed) {
        try {
          const response = await fetch('/admin/approve', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to approve user');
          }
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, login_possible: data[0].login_possible };
            }
            return user;
          }));
            alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 사용승인이 완료 되었습니다.`);
            // 로그인 가능한 경우에 필요한 작업 수행
         } catch (error) {
          console.error('Error approving user:', error);
        }
      };
  };

  const handleRevoke = async (uid,nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님을 사용해제 처리하시겠습니까?`);

    if (confirmed) {
      try {
        const response = await fetch('/admin/revoke', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        });
        if (!response.ok) {
          throw new Error('Failed to revoke user');
        }

        const data = await response.json();
        // 해당 사용자의 정보만을 업데이트합니다.
        if (data.login_possible === 'revoke') {
          setUsers(prevUsers => prevUsers.filter(user => user.uid !== uid));
        } 
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 사용 해제가 완료 되었습니다.`);
          return users;
        } catch (error) {
        console.error('Error revoking user:', error);
      }
    };
  };

  ////////////////// Leverage 승인여부 //////////////////
  const handle_Leverage_Approve = async (uid, nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Leverage version 프로그램 사용 승인하시겠습니까?`);

    if (confirmed) {
        try {
          const response = await fetch('/admin/approve_leverage', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to approve leverage_program');
          }
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, leverage_possible: data[0].leverage_possible };
            }
            return user;
          }));
            alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Leverage version 프로그램 사용승인이 완료 되었습니다.`);
            // 로그인 가능한 경우에 필요한 작업 수행
         } catch (error) {
          console.error('Error approving user:', error);
        }
      };
  };

  const handle_Leverage_Revoke = async (uid,nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 leverage version 프로그램 사용 해제하시겠습니까?`);

    if (confirmed) {
      try {
        const response = await fetch('/admin/revoke_leverage', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        });
        if (!response.ok) {
          throw new Error('Failed to revoke user');
        }

        const data = await response.json();
        // 해당 사용자의 정보만을 업데이트합니다.
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.uid === uid) {
            return { ...user, leverage_possible: data[0].leverage_possible };
          }
          return user;
        }));
        alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 leverage version 프로그램 사용 해제가 완료 되었습니다.`);
        } catch (error) {
        console.error('Error revoking user:', error);
      }
    };
  };

  ////////////////// Royal 승인여부 //////////////////
  const handle_Royal_Approve = async (uid, nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Royal version 프로그램 사용 승인하시겠습니까?`);

    if (confirmed) {
        try {
          const response = await fetch('/admin/approve_royal', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to approve royal_program');
          }
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, royal_possible: data[0].royal_possible };
            }
            return user;
          }));
            alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Royal version 프로그램 사용승인이 완료 되었습니다.`);
            // 로그인 가능한 경우에 필요한 작업 수행
         } catch (error) {
          console.error('Error approving user:', error);
        }
      };
  };

  const handle_Royal_Revoke = async (uid,nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Royal version 프로그램 사용 해제하시겠습니까?`);

    if (confirmed) {
      try {
        const response = await fetch('/admin/revoke_royal', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        });
        if (!response.ok) {
          throw new Error('Failed to revoke user');
        }

        const data = await response.json();
        // 해당 사용자의 정보만을 업데이트합니다.
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.uid === uid) {
            return { ...user, royal_possible: data[0].royal_possible };
          }
          return user;
        }));
        alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Royal version 프로그램 사용 해제가 완료 되었습니다.`);
        } catch (error) {
        console.error('Error revoking user:', error);
      }
    };
  };

  ////////////////// KP 승인여부 //////////////////
  const handle_KP_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 KP version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_kp', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve kp_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, kp_possible: data[0].kp_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 KP version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };
  
  const handle_KP_Revoke = async (uid,nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 KP version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_kp', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, kp_possible: data[0].kp_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 KP version 프로그램 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };
  
  ////////////////// wz 승인여부 //////////////////
  const handle_WZ_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Wizard version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_wz', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve wz_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, wz_possible: data[0].wz_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Wizard version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };
  
  const handle_WZ_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Wizard version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_wz', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, wz_possible: data[0].wz_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Wizard version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };

  ////////////////// rv 승인여부 //////////////////
  const handle_RV_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Revolution version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_rv', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve rv_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, rv_possible: data[0].rv_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Revolution version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };
  
  const handle_RV_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Revolution version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_rv', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, rv_possible: data[0].rv_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Revolution version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };


  ////////////////// ultra 승인여부 //////////////////
  const handle_ULTRA_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Revolution version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_ultra', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve ultra_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, ultra_possible: data[0].ultra_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Revolution version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };
  
  const handle_ULTRA_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Revolution version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_ultra', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, ultra_possible: data[0].ultra_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Revolution version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };
  

  ////////////////// meme 승인여부 //////////////////
  const handle_MEME_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Meme(스캔) 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_meme', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve meme_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, meme_possible: data[0].meme_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Meme(스캔) 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };

  const handle_MEME_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Meme(스캔) 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_meme', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, meme_possible: data[0].meme_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Meme(스캔) 프로그램 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };

  ////////////////// escape 승인여부 //////////////////
  const handle_ESCAPE_Approve = async (uid, nickname) => {
    const confirmed = window.confirm(
      `UID : ${uid} / NICKNAME : ${nickname} 님의 Escape version 프로그램 사용 승인하시겠습니까?`
    );

    if (confirmed) {
      try {
        const response = await fetch('/admin/approve_escape', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        });
        if (!response.ok) {
          throw new Error('Failed to approve escape_program');
        }
        const data = await response.json();
        setUsers((prevUsers) =>
          prevUsers.map((user) => {
            if (user.uid === uid) {
              return { ...user, escape_possible: data[0].escape_possible };
            }
            return user;
          })
        );
        alert(
          `UID : ${uid} / NICKNAME : ${nickname} 님의 Escape version 프로그램 사용승인이 완료 되었습니다.`
        );
      } catch (error) {
        console.error('Error approving user:', error);
      }
    }
  };

  const handle_ESCAPE_Revoke = async (uid, nickname) => {
    const confirmed = window.confirm(
      `UID : ${uid} / NICKNAME : ${nickname} 님의 Escape version 프로그램 사용 해제하시겠습니까?`
    );

    if (confirmed) {
      try {
        const response = await fetch('/admin/revoke_escape', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        });
        if (!response.ok) {
          throw new Error('Failed to revoke escape_program');
        }
        const data = await response.json();
        setUsers((prevUsers) =>
          prevUsers.map((user) => {
            if (user.uid === uid) {
              return { ...user, escape_possible: data[0].escape_possible };
            }
            return user;
          })
        );
        alert(
          `UID : ${uid} / NICKNAME : ${nickname} 님의 Escape version 프로그램 사용 해제가 완료 되었습니다.`
        );
      } catch (error) {
        console.error('Error revoking user:', error);
      }
    }
  };

   ////////////////// hedge 승인여부 //////////////////
  const handle_HEDGE_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hedge version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_hedge', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve hedge_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, hedge_possible: data[0].hedge_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hedge version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };

  const handle_HEDGE_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 hedge version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_hedge', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, hedge_possible: data[0].hedge_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 hedge version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };

  ////////////////// circle 승인여부 //////////////////
  const handle_CIRCLE_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Circle version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_circle', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve circle_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, circle_possible: data[0].circle_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Circle version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };

  const handle_CIRCLE_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 circle version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_circle', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, circle_possible: data[0].circle_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 circle version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };

  ////////////////// csupreme 승인여부 //////////////////
  const handle_CIRCLESUPREME_Approve = async (uid, nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Circle Pro version 프로그램 사용 승인하시겠습니까?`);

    if (confirmed) {
        try {
          const response = await fetch('/admin/approve_csupreme', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to approve csupreme_program');
          }
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, csupreme_possible: data[0].csupreme_possible };
            }
            return user;
          }));
            alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Circle Pro version 프로그램 사용승인이 완료 되었습니다.`);
            // 로그인 가능한 경우에 필요한 작업 수행
          } catch (error) {
          console.error('Error approving user:', error);
        }
      };
  };

  const handle_CIRCLESUPREME_Revoke = async (uid,nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Circle Pro version 프로그램 사용 해제하시겠습니까?`);

    if (confirmed) {
      try {
        const response = await fetch('/admin/revoke_csupreme', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        });
        if (!response.ok) {
          throw new Error('Failed to revoke user');
        }

        const data = await response.json();
        // 해당 사용자의 정보만을 업데이트합니다.
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.uid === uid) {
            return { ...user, csupreme_possible: data[0].csupreme_possible };
          }
          return user;
        }));
        alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Circle Pro version 프로그 사용 해제가 완료 되었습니다.`);
        } catch (error) {
        console.error('Error revoking user:', error);
      }
    };
  };

  ////////////////// cplus 승인여부 //////////////////
  const handle_CPLUS_Approve = async (uid, nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Cplus version 프로그램 사용 승인하시겠습니까?`);

    if (confirmed) {
        try {
          const response = await fetch('/admin/approve_cplus', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to approve Cplus_program');
          }
          const data = await response.json();
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, cplus_possible: data[0].cplus_possible };
            }
            return user;
          }));
            alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Cplus version 프로그램 사용승인이 완료 되었습니다.`);
         } catch (error) {
          console.error('Error approving user:', error);
        }
      };
  };

  const handle_CPLUS_Revoke = async (uid,nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Cplus version 프로그램 사용 해제하시겠습니까?`);

    if (confirmed) {
      try {
        const response = await fetch('/admin/revoke_cplus', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        });
        if (!response.ok) {
          throw new Error('Failed to revoke user');
        }

        const data = await response.json();
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.uid === uid) {
            return { ...user, cplus_possible: data[0].cplus_possible };
          }
          return user;
        }));
        alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Cplus version 프로그 사용 해제가 완료 되었습니다.`);
        } catch (error) {
        console.error('Error revoking user:', error);
      }
    };
  };

  ////////////////// hold 승인여부 //////////////////
  const handle_HOLD_Approve = async (uid, nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hold version 프로그램 사용 승인하시겠습니까?`);

    if (confirmed) {
        try {
          const response = await fetch('/admin/approve_hold', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to approve hold_program');
          }
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, hold_possible: data[0].hold_possible };
            }
            return user;
          }));
            alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hold version 프로그램 사용승인이 완료 되었습니다.`);
            // 로그인 가능한 경우에 필요한 작업 수행
          } catch (error) {
          console.error('Error approving user:', error);
        }
      };
  };

  const handle_HOLD_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hold version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_hold', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, hold_possible: data[0].hold_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hold version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
  };

  ////////////////// band 승인여부 //////////////////
  const handle_BAND_Approve = async (uid, nickname) => {
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Band version 프로그램 사용 승인하시겠습니까?`);

    if (confirmed) {
        try {
          const response = await fetch('/admin/approve_band', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to approve band_program');
          }
          const data = await response.json();
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, band_possible: data[0].band_possible };
            }
            return user;
          }));
            alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Band version 프로그램 사용승인이 완료 되었습니다.`);
          } catch (error) {
          console.error('Error approving user:', error);
        }
      };
  };

  const handle_BAND_Revoke = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Band version 프로그램 사용 해제하시겠습니까?`);

      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_band', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke band user');
          }

          const data = await response.json();
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, band_possible: data[0].band_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Band version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };

  ////////////////// circle PRO 승인여부 //////////////////
  const handle_CIRCLEPRO_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hedge PRO version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_circlepro', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve circlePRO_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, circlepro_possible: data[0].circlepro_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hedge PRO version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };

  const handle_CIRCLEPRO_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hedge PRO version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_circlepro', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, circlepro_possible: data[0].circlepro_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Hedge PRO version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };


  ////////////////// burst 승인여부 //////////////////
  const handle_BURST_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Burst version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_burst', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve burst_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, burst_possible: data[0].burst_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Burst version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };

  const handle_BURST_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 burst version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_burst', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, burst_possible: data[0].burst_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 burst version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };


  ////////////////// pin 승인여부 //////////////////
  const handle_PIN_Approve = async (uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Pin version 프로그램 사용 승인하시겠습니까?`);
  
      if (confirmed) {
          try {
            const response = await fetch('/admin/approve_pin', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ uid }),
            });
            if (!response.ok) {
              throw new Error('Failed to approve pin_program');
            }
            const data = await response.json();
            // 해당 사용자의 정보만을 업데이트합니다.
            setUsers(prevUsers => prevUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, pin_possible: data[0].pin_possible };
              }
              return user;
            }));
              alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 Pin version 프로그램 사용승인이 완료 되었습니다.`);
              // 로그인 가능한 경우에 필요한 작업 수행
           } catch (error) {
            console.error('Error approving user:', error);
          }
        };
    };

  const handle_PIN_Revoke = async (uid,nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 Pin version 프로그램 사용 해제하시겠습니까?`);
  
      if (confirmed) {
        try {
          const response = await fetch('/admin/revoke_pin', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
          });
          if (!response.ok) {
            throw new Error('Failed to revoke user');
          }
  
          const data = await response.json();
          // 해당 사용자의 정보만을 업데이트합니다.
          setUsers(prevUsers => prevUsers.map(user => {
            if (user.uid === uid) {
              return { ...user, pin_possible: data[0].pin_possible };
            }
            return user;
          }));
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 pin version 프로그 사용 해제가 완료 되었습니다.`);
          } catch (error) {
          console.error('Error revoking user:', error);
        }
      };
    };
  
    const handleFeatureConnect = async (feature, uid, nickname) => {
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 ${feature.label} program으로 연결할까요?`);
      if (!confirmed) {
        return;
      }
    
      setTransferLoadingByUid(prev => ({ ...prev, [uid]: true }));
      try {
        const response = await fetch('/admin/validate_bitget_api_keys', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        });
        const data = await response.json().catch(() => ({}));
    
        if (!response.ok || !data.ok) {
          alert(data.message || 'API 키 검증에 실패했습니다.');
          return;
        }
    
        alert(data.message || '등록된 API키의 정상동작을 확인완료');
        document.cookie = `admin_target_uid=${encodeURIComponent(uid)}; path=/; SameSite=Lax`;
        sessionStorage.setItem('adminAccessContext', JSON.stringify({
          uid: uid || '',
          feature: feature.key,
          from_admin: 1,
        }));

        if (feature.key === 'hold') {
          sessionStorage.setItem('holdAccessContext', JSON.stringify({
            uid: uid || '',
            from_admin: 1,
          }));
        }

        if (feature.key === 'band') {
          sessionStorage.setItem('bandAccessContext', JSON.stringify({
            uid: uid || '',
            from_admin: 1,
          }));
        }

        if (feature.key === 'meme') {
          sessionStorage.setItem('memeAccessContext', JSON.stringify({
            uid: uid || '',
            from_admin: 1,
          }));
        }

        window.location.href = feature.redirect;
      } catch (error) {
        console.error(`${feature.label} 연결 처리 오류:`, error);
        alert('연결 처리 중 오류가 발생했습니다.');
      } finally {
        setTransferLoadingByUid(prev => ({ ...prev, [uid]: false }));
      }
    };
  
    ////////////////// 이메일 변경 ////////////////////
  const handleChangeEmail = async (uid, nickname) => {
      const newEmail = window.prompt(`UID : ${uid} / NICKNAME : ${nickname} 님의 새 이메일을 입력하세요:`);
    
      if (!newEmail) return; // 사용자가 취소를 누르면 아무 작업도 하지 않음
    
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 e-mail을 "${newEmail}"(으)로 변경하시겠습니까?`);
    
      if (confirmed) {
        try {
          const response = await fetch('/admin/change_email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, email: newEmail }), // 새 이메일 전송
          });
    
          if (!response.ok) throw new Error('Failed to change email');
    
          const data = await response.json();
    
          setUsers(prevUsers => prevUsers.map(user => 
            user.uid === uid ? { ...user, email: data[0].email } : user
          ));
    
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 e-mail 정보가 변경 완료 되었습니다.`);
        } catch (error) {
          console.error('Error changing email:', error);
        }
      }
    };
    
    ////////////////// 비밀번호 변경 ////////////////////
  const handleChangePassword = async (uid, nickname) => {
      const newPassword = window.prompt(`UID : ${uid} / NICKNAME : ${nickname} 님의 새 비밀번호를 입력하세요:`);
    
      if (!newPassword) return; // 사용자가 취소를 누르면 아무 작업도 하지 않음
    
      const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 비밀번호를 "${newPassword}"(으)로 변경하시겠습니까?`);
    
      if (confirmed) {
        try {
          const response = await fetch('/admin/change_password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, pw: newPassword }), // 새 이메일 전송
          });
    
          if (!response.ok) throw new Error('Failed to change password');
    
          const data = await response.json();
    
          setUsers(prevUsers => prevUsers.map(user => 
            user.uid === uid ? { ...user, pw: data[0].pw } : user
          ));
    
          alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 password 정보가 변경 완료 되었습니다.`);
        } catch (error) {
          console.error('Error changing email:', error);
        }
      }
    };
  
  ////////////////// 닉네임 변경 ////////////////////
  const handleChangeNickname = async (uid, nickname) => {
    const newNickname = window.prompt(`UID : ${uid} / NICKNAME : ${nickname} 님의 새 닉네임을 입력하세요:`);
  
    if (!newNickname) return; // 사용자가 취소를 누르면 아무 작업도 하지 않음
  
    const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님의 닉네임을 "${newNickname}"(으)로 변경하시겠습니까?`);
  
    if (confirmed) {
      try {
        const response = await fetch('/admin/change_nickname', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid, nickname: newNickname }), // 새 Nickname 전송
        });
  
        if (!response.ok) throw new Error('Failed to change nickname');
  
        const data = await response.json();
  
        setUsers(prevUsers => prevUsers.map(user => 
          user.uid === uid ? { ...user, nickname: data[0].nickname } : user
        ));
  
        alert(`UID : ${uid} 님의 NICKNAME 정보가 변경 완료 되었습니다.`);
      } catch (error) {
        console.error('Error changing nickname:', error);
      }
    }
  };

  const handleFetchFuturesBalance = async (uid) => {
    const targetUser = users.find((user) => user.uid === uid);
    const confirmed = window.confirm(
      `UID : ${uid}${targetUser?.nickname ? ` / NICKNAME : ${targetUser.nickname}` : ""} 님의 선물잔고를 조회하고 저장하시겠습니까?`
    );

    if (!confirmed) {
      return;
    }

    setBalanceLoadingByUid((prevState) => ({
      ...prevState,
      [uid]: true,
    }));
    setBalanceSaveLoadingByUid((prevState) => ({
      ...prevState,
      [uid]: true,
    }));

    try {
      const response = await fetch("/admin/futures_balance", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uid }),
      });

      let result;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const message = await response.text();
        result = { ok: response.ok, message };
      }

      if (!response.ok || !result.ok) {
        throw new Error(result.message || "선물잔고 조회에 실패했습니다.");
      }

      setBalanceByUid((prevState) => ({
        ...prevState,
        [uid]: {
          mainBalance: result.main_balance,
          secondBalance: result.second_balance,
          totalBalance: result.total_balance,
          uid2: result.uid_2,
          usePassUsdtOnly: false,
        },
      }));

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.uid === uid
            ? {
                ...user,
                first_usdt: result.main_balance,
                sec_usdt: result.second_balance,
              }
            : user
        )
      );

      const saveResponse = await fetch("/admin/save_pass_usdt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uid,
          pass_usdt: result.total_balance,
        }),
      });

      let saveResult;
      const saveContentType = saveResponse.headers.get("content-type") || "";

      if (saveContentType.includes("application/json")) {
        saveResult = await saveResponse.json();
      } else {
        const message = await saveResponse.text();
        saveResult = { ok: saveResponse.ok, message };
      }

      if (!saveResponse.ok || !saveResult.ok) {
        throw new Error(saveResult.message || "선물잔고 저장에 실패했습니다.");
      }

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.uid === uid
            ? {
                ...user,
                pass_usdt: saveResult.pass_usdt ?? result.total_balance,
                current_usdt: saveResult.current_usdt ?? user.current_usdt,
                rate_return: saveResult.rate_return ?? user.rate_return,
              }
            : user
        )
      );

      alert(saveResult.message || "선물잔고 저장이 완료되었습니다.");
    } catch (error) {
      console.error("선물잔고 조회 오류:", error);
      alert(error.message || "선물잔고 조회 중 오류가 발생했습니다.");
    } finally {
      setBalanceLoadingByUid((prevState) => ({
        ...prevState,
        [uid]: false,
      }));
      setBalanceSaveLoadingByUid((prevState) => ({
        ...prevState,
        [uid]: false,
      }));
    }
  };

  const handleFetchCurrentFuturesBalance = async (uid) => {
    const targetUser = users.find((user) => user.uid === uid);
    const confirmed = window.confirm(
      `UID : ${uid}${targetUser?.nickname ? ` / NICKNAME : ${targetUser.nickname}` : ""} 님의 현재 선물잔고를 재조회하시겠습니까?`
    );

    if (!confirmed) {
      return;
    }

    setCurrentBalanceLoadingByUid((prevState) => ({
      ...prevState,
      [uid]: true,
    }));

    try {
      const response = await fetch("/admin/current_futures_balance", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uid }),
      });

      let result;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const message = await response.text();
        result = { ok: response.ok, message };
      }

      if (!response.ok || !result.ok) {
        throw new Error(result.message || "현재 선물잔고 조회에 실패했습니다.");
      }

      setCurrentBalanceByUid((prevState) => ({
        ...prevState,
        [uid]: {
          mainBalance: result.current_main_balance,
          secondBalance: result.current_second_balance,
          totalBalance: result.current_total_balance,
          uid2: result.uid_2,
        },
      }));

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.uid === uid
            ? {
                ...user,
                first_usdt_current: result.current_main_balance,
                sec_usdt_current: result.current_second_balance,
                first_available_current: result.current_main_available,
                sec_available_current: result.current_second_available,
                current_usdt: result.current_total_balance,
                rate_return: result.rate_return,
              }
            : user
        )
      );

      alert(result.message || "현재 선물잔고 재조회가 완료되었습니다.");
    } catch (error) {
      console.error("현재 선물잔고 조회 오류:", error);
      alert(error.message || "현재 선물잔고 조회 중 오류가 발생했습니다.");
    } finally {
      setCurrentBalanceLoadingByUid((prevState) => ({
        ...prevState,
        [uid]: false,
      }));
    }
  };

  const parseBalanceInput = (value) => {
    const trimmed = String(value ?? "").trim().replace(/,/g, "");
    if (trimmed === "") {
      return null;
    }

    const parsedValue = Number(trimmed);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const openManualBalanceModal = (user) => {
    const currentBalance = balanceByUid[user.uid] || {};

    setManualBalanceModal({
      isOpen: true,
      uid: user.uid,
      uid2: user.uid_2 || "",
      nickname: user.nickname || "",
      mainBalance: String(currentBalance.mainBalance ?? user.first_usdt ?? ""),
      secondBalance: String(currentBalance.secondBalance ?? user.sec_usdt ?? ""),
    });
  };

  const closeManualBalanceModal = () => {
    setManualBalanceModal({
      isOpen: false,
      uid: "",
      uid2: "",
      nickname: "",
      mainBalance: "",
      secondBalance: "",
    });
  };

  const handleManualBalanceFieldChange = (field) => (event) => {
    const { value } = event.target;
    setManualBalanceModal((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleManualSaveBalance = async () => {
    const mainBalance = parseBalanceInput(manualBalanceModal.mainBalance);
    const secondBalance = parseBalanceInput(manualBalanceModal.secondBalance);

    if (mainBalance === null || secondBalance === null) {
      alert("메인계정과 보조계정 잔고는 숫자로 입력해주세요.");
      return;
    }

    setBalanceSaveLoadingByUid((prevState) => ({
      ...prevState,
      [manualBalanceModal.uid]: true,
    }));

    try {
      const response = await fetch("/admin/save_manual_futures_balance", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uid: manualBalanceModal.uid,
          main_balance: mainBalance,
          second_balance: secondBalance,
        }),
      });

      let result;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const message = await response.text();
        result = { ok: response.ok, message };
      }

      if (!response.ok || !result.ok) {
        throw new Error(result.message || "수동 잔고 저장에 실패했습니다.");
      }

      setBalanceByUid((prevState) => ({
        ...prevState,
        [manualBalanceModal.uid]: {
          mainBalance: result.main_balance,
          secondBalance: result.second_balance,
          totalBalance: result.total_balance,
          uid2: result.uid_2,
          usePassUsdtOnly: false,
        },
      }));

      setUsers((prevUsers) =>
        prevUsers.map((currentUser) =>
          currentUser.uid === manualBalanceModal.uid
            ? {
                ...currentUser,
                first_usdt: result.main_balance,
                sec_usdt: result.second_balance,
                pass_usdt: result.total_balance,
                current_usdt: result.current_usdt ?? currentUser.current_usdt,
                rate_return: result.rate_return ?? currentUser.rate_return,
              }
            : currentUser
        )
      );

      closeManualBalanceModal();
      alert(result.message || "수동 잔고 저장이 완료되었습니다.");
    } catch (error) {
      console.error("수동 잔고 저장 오류:", error);
      alert(error.message || "수동 잔고 저장 중 오류가 발생했습니다.");
    } finally {
      setBalanceSaveLoadingByUid((prevState) => ({
        ...prevState,
        [manualBalanceModal.uid]: false,
      }));
    }
  };

  const toggleBalanceDetails = (uid) => {
    if (!balanceByUid[uid]) {
      return;
    }

    setExpandedBalanceByUid((prevState) => ({
      ...prevState,
      [uid]: !prevState[uid],
    }));
  };

  const openApiChangeModal = (user, type) => {
    setApiChangeModal({
      isOpen: true,
      type,
      uid: user.uid,
      nickname: user.nickname || "",
      api: "",
      secretkey: "",
      apiPw: "",
    });
  };

  const closeApiChangeModal = () => {
    setApiChangeModal({
      isOpen: false,
      type: "main",
      uid: "",
      nickname: "",
      api: "",
      secretkey: "",
      apiPw: "",
    });
  };

  const handleApiChangeField = (field) => (event) => {
    const { value } = event.target;
    setApiChangeModal((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleSaveApiChange = async () => {
    const api = String(apiChangeModal.api || "").trim();
    const secretkey = String(apiChangeModal.secretkey || "").trim();
    const apiPw = String(apiChangeModal.apiPw || "").trim();

    if (!api || !secretkey || !apiPw) {
      alert("API, SecretKey, API PW를 모두 입력해주세요.");
      return;
    }

    setApiUpdateLoadingByUid((prevState) => ({
      ...prevState,
      [apiChangeModal.uid]: true,
    }));

    try {
      const endpoint =
        apiChangeModal.type === "main"
          ? "/admin/change_main_api_keys"
          : "/admin/change_second_api_keys";

      const payload =
        apiChangeModal.type === "main"
          ? {
              uid: apiChangeModal.uid,
              api,
              secretkey,
              api_pw: apiPw,
            }
          : {
              uid: apiChangeModal.uid,
              api_2: api,
              secretkey_2: secretkey,
              api_pw_2: apiPw,
            };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let result;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const message = await response.text();
        result = { ok: response.ok, message };
      }

      if (!response.ok || !result.ok) {
        throw new Error(result.message || "API 키 변경에 실패했습니다.");
      }

      closeApiChangeModal();
      alert(result.message || "API 키 변경이 완료되었습니다.");
    } catch (error) {
      console.error("API 키 변경 오류:", error);
      alert(error.message || "API 키 변경 중 오류가 발생했습니다.");
    } finally {
      setApiUpdateLoadingByUid((prevState) => ({
        ...prevState,
        [apiChangeModal.uid]: false,
      }));
    }
  };

  const featureConfigs = buildAdminFeatureConfigs({
    handle_WZ_Approve,
    handle_WZ_Revoke,
    handle_HEDGE_Approve,
    handle_HEDGE_Revoke,
    handle_CIRCLE_Approve,
    handle_CIRCLE_Revoke,
    handle_CIRCLEPRO_Approve,
    handle_CIRCLEPRO_Revoke,
    handle_CIRCLESUPREME_Approve,
    handle_CIRCLESUPREME_Revoke,
    handle_CPLUS_Approve,
    handle_CPLUS_Revoke,
    handle_HOLD_Approve,
    handle_HOLD_Revoke,
    handle_BAND_Approve,
    handle_BAND_Revoke,
    handle_MEME_Approve,
    handle_MEME_Revoke,
    handle_ESCAPE_Approve,
    handle_ESCAPE_Revoke,
  });

//    ///////////// 그룹 승인 여부 ///////////////

//   const handleGroup = async (uid, nickname) => {
//     const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님을 별도 그룹으로 처리 하시겠습니까?`);

//     if (confirmed) {
//         try {
//           const response = await fetch('/admin/group', {
//             method: 'POST',
//             headers: {
//               'Content-Type': 'application/json',
//             },
//             body: JSON.stringify({ uid }),
//           });
//           if (!response.ok) {
//             throw new Error('Failed to group user');
//           }
//           const data = await response.json();
//           // 해당 사용자의 정보만을 업데이트합니다.
//           setUsers(prevUsers => prevUsers.map(user => {
//             if (user.uid === uid) {
//               return { ...user, another_group: data[0].another_group };
//             }
//             return user;
//           }));
//           alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 그룹 처리가 완료 되었습니다.`);
//            // 로그인 가능한 경우에 필요한 작업 수행
//          } catch (error) {
//           console.error('Error approving user:', error);
//         }
//       };
//   };

//   const handleGroupRevoke = async (uid,nickname) => {
//     const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님을 그룹해제 처리하시겠습니까?`);

//     if (confirmed) {
//       try {
//         const response = await fetch('/admin/grouprevoke', {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({ uid }),
//         });
//         if (!response.ok) {
//           throw new Error('Failed to revoke user');
//         }

//         const data = await response.json();
//         // 해당 사용자의 정보만을 업데이트합니다.
//         setUsers(prevUsers => prevUsers.map(user => {
//           if (user.uid === uid) {
//             return { ...user, another_group: data[0].another_group };
//           }
//           return user;
//         }));
//         alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 그룹 해재 처리가 완료 되었습니다.`);
//       } catch (error) {
//         console.error('Error group approving user:', error);
//     };
//   };
// };
  

//   const handleSuperuserapproval = async (uid, nickname) => {
//     const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님을 슈퍼 유저 승인처리하시겠습니까?`);
  
//     if (confirmed) {
//       try {
//         const response = await fetch('/admin/superuserapproval', {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({ uid }),
//         });
//         if (!response.ok) {
//           throw new Error('Failed to approve user');
//         }
  
//         const data = await response.json();
//         // 해당 사용자의 정보만을 업데이트합니다.
//         setUsers(prevUsers => prevUsers.map(user => {
//           if (user.uid === uid) {
//             return { ...user, super_user: data[0].super_user };
//           }
//           return user;
//         }));
  
//         alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 슈퍼 유저 승인이 완료 되었습니다.`);
//       } catch (error) {
//         console.error('Error revoking user:', error);
//       }
//     };
//   };
  

//   const handleSuperuserrevoke = async (uid,nickname) => {
//     const confirmed = window.confirm(`UID : ${uid} / NICKNAME : ${nickname} 님을 슈퍼유저 승인 해제 처리하시겠습니까?`);

//     if (confirmed) {
//       try {
//         const response = await fetch('/admin/superuserrevoke', {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({ uid }),
//         });
//         if (!response.ok) {
//           throw new Error('Failed to revoke user');
//         }
        
//         const data = await response.json();
//         // 해당 사용자의 정보만을 업데이트합니다.
//         setUsers(prevUsers => prevUsers.map(user => {
//           if (user.uid === uid) {
//             return { ...user, super_user: data[0].super_user };
//           }
//           return user;
//         }));
//         // 승인 해제가 성공적으로 처리되었을 때 필요한 작업 수행
//         alert(`UID : ${uid} / NICKNAME : ${nickname} 님의 슈퍼 유저 승인 해체 처리 되었습니다.`);
//       } catch (error) {
//         console.error('Error revoking user:', error);
//       }
//     };
//   };

// 승인 상태 텍스트
const getApprovalLabel = (user) =>
    user.login_possible === "1" || user.login_possible === 1
      ? "승인"
      : "대기";
  
  const getGrantedFeatureTags = (user) =>
    featureConfigs
      .filter(
        ({ possibleKey }) =>
          user[possibleKey] === "1" || user[possibleKey] === 1
      )
      .map(({ tag }) => tag);
  
  const getSelectedFeature = (user) =>
    featureConfigs.find(({ key }) => key === selectedFeatureByUid[user.uid]) ||
    featureConfigs.find(({ key }) => key === "hold") ||
    featureConfigs[0];
  
  const handleSelectedFeatureChange = (uid) => (event) => {
    const { value } = event.target;
    setSelectedFeatureByUid((prevState) => ({
      ...prevState,
      [uid]: value,
    }));
  };
  
  const getSelectedAccountAction = (uid) => accountActionByUid[uid] || "approve";
  
  const handleAccountActionChange = (uid) => (event) => {
    const { value } = event.target;
    setAccountActionByUid((prevState) => ({
      ...prevState,
      [uid]: value,
    }));
  };
  
  const executeAccountAction = (user) => {
    const action = getSelectedAccountAction(user.uid);
  
    if (action === "approve") {
      handleApprove(user.uid, user.nickname);
      return;
    }
  
    if (action === "revoke") {
      handleRevoke(user.uid, user.nickname);
    }
  };
  
  const getSelectedInfoAction = (user) => infoActionByUid[user.uid] || "nickname";
  
  const handleInfoActionChange = (uid) => (event) => {
    const { value } = event.target;
    setInfoActionByUid((prevState) => ({
      ...prevState,
      [uid]: value,
    }));
  };
  
  const executeInfoAction = (user) => {
    const action = getSelectedInfoAction(user);
  
    if (action === "email") {
      handleChangeEmail(user.uid, user.nickname);
      return;
    }
  
    if (action === "password") {
      handleChangePassword(user.uid, user.nickname);
      return;
    }
  
    if (action === "nickname") {
      handleChangeNickname(user.uid, user.nickname);
      return;
    }
  
    if (action === "main_api") {
      openApiChangeModal(user, "main");
      return;
    }
  
    if (action === "second_api") {
      openApiChangeModal(user, "second");
    }
  };
  
  //로그아웃
  const handleLogout = () => {
    const uuid = cookies.uuid; // cookies에서 uuid를 얻어옴
    if (!uuid) {
      alert('로그인 정보가 존재하지 않습니다.');
      return;
    }
    // 로그아웃 요청을 서버로 보냅니다.
    fetch('/super_logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId: uuid })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('로그아웃 요청 실패');
      }
      console.log('로그아웃 성공');
      alert('Logout is successful.');
      window.location.href = '/AdminLogin';
    })
    .catch(error => {
      console.error('로그아웃 요청 중 오류 발생:', error);
    });
  };
  
  // 셋팅페이지로 이동
  // ───── 선물 ↔ 현물 Transfer 모달 ─────
  const openFuturesSpotTransferModal = (user) => {
    setFuturesSpotTransferModal({
      isOpen: true,
      uid: user.uid,
      uid2: user.uid_2 || "",
      nickname: user.nickname || "",
      account: "main",
      direction: "futures_to_spot",
      amount: "",
      balanceInfo: null,
      balanceLoading: false,
    });
    fetchTransferBalance(user.uid, "main");
  };

  const closeFuturesSpotTransferModal = () => {
    setFuturesSpotTransferModal({
      isOpen: false,
      uid: "",
      uid2: "",
      nickname: "",
      account: "main",
      direction: "futures_to_spot",
      amount: "",
      balanceInfo: null,
      balanceLoading: false,
    });
  };

  const handleFuturesSpotTransferFieldChange = (field) => (event) => {
    const { value } = event.target;
    setFuturesSpotTransferModal((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "account" ? { balanceInfo: null } : {}),
    }));
    if (field === "account") {
      fetchTransferBalance(futuresSpotTransferModal.uid, value);
    }
  };

  const fetchTransferBalance = async (uid, account) => {
    setFuturesSpotTransferModal((prev) => ({ ...prev, balanceLoading: true, balanceInfo: null }));
    try {
      const response = await fetch("/admin/transfer_account_balance", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, account }),
      });
      const result = await response.json();
      if (!result.ok) throw new Error(result.message || "잔고 조회 실패");
      setFuturesSpotTransferModal((prev) => ({
        ...prev,
        balanceLoading: false,
        balanceInfo: {
          futures: result.futures_balance,
          spot: result.spot_balance,
          forAccount: account,
        },
      }));
    } catch (error) {
      alert(error.message || "잔고 조회 중 오류가 발생했습니다.");
      setFuturesSpotTransferModal((prev) => ({ ...prev, balanceLoading: false }));
    }
  };

  const handleSubmitFuturesSpotTransfer = async () => {
    const amountStr = String(futuresSpotTransferModal.amount || "").trim();
    const amount = parseFloat(amountStr);
    if (!amountStr || isNaN(amount) || amount <= 0) {
      alert("이동할 USDT 수량을 숫자로 입력해주세요.");
      return;
    }

    setFuturesSpotLoadingByUid((prev) => ({ ...prev, [futuresSpotTransferModal.uid]: true }));

    try {
      const response = await fetch("/admin/futures_spot_transfer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: futuresSpotTransferModal.uid,
          account: futuresSpotTransferModal.account,
          direction: futuresSpotTransferModal.direction,
          amount,
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      const result = contentType.includes("application/json")
        ? await response.json()
        : { ok: response.ok, message: await response.text() };

      if (!response.ok || !result.ok) {
        throw new Error(result.message || "USDT 이동에 실패했습니다.");
      }

      closeFuturesSpotTransferModal();
      alert(result.message || "USDT 이동이 완료되었습니다.");
    } catch (error) {
      console.error("선물↔현물 USDT 이동 오류:", error);
      alert(error.message || "USDT 이동 중 오류가 발생했습니다.");
    } finally {
      setFuturesSpotLoadingByUid((prev) => ({ ...prev, [futuresSpotTransferModal.uid]: false }));
    }
  };
  // ───────────────────────────────────────

  const handleMoveBinance = () => {
    const uuid = cookies.uuid;
    if (!uuid) {
      alert('로그인 정보가 존재하지 않습니다.');
      return;
    }
    window.location.href = '/AdminpageBinance';
  };

// const marginSet = (margin) => {

//   const confirmed = window.confirm(margin + ' 값으로 Leverage version 익절 기준을 변경하시겠습니까?');

//   if (confirmed) {
//     fetch(`/marginset_leverage/${margin}`)
//     .then((response) => {
//       if(!response.ok) {
//         throw new Error('400 아니면 500 에러남');
//       }
//       return response.json()
//     })
//     .then((result) => {
//       console.log('margin set 서버에서 받아온 데이터');
//     })
//     .catch((error) => {
//       console.log('에러남',error);
//     })
//     window.location.reload()
//     // onClickSearch('default');
//   };

// }

  const filteredUsers = filterUsersBySearch(users, searchQuery);
  const orderedUsers = filteredUsers.slice().reverse();

  const updateRowIdentityPeeks = useCallback(() => {
    const host = userTableHostRef.current;
    const scrollEl = userTableScrollRef.current;
    if (!host || !scrollEl || window.innerWidth > ADMIN_USER_TABLE_MOBILE_MAX_PX) {
      setRowIdentityPeeks([]);
      return;
    }

    const hostRect = host.getBoundingClientRect();
    const wrapRect = scrollEl.getBoundingClientRect();
    const rows = scrollEl.querySelectorAll("tbody tr[data-admin-uid]");
    const nextPeeks = [];

    for (const row of rows) {
      const rowRect = row.getBoundingClientRect();
      if (rowRect.bottom <= wrapRect.top + 6 || rowRect.top >= wrapRect.bottom - 6) {
        continue;
      }

      const uidCell = row.querySelector(".admin-dashboard__cell-uid");
      const nickCell = row.querySelector(".admin-dashboard__cell-nick");
      if (!uidCell) continue;

      const uidRect = uidCell.getBoundingClientRect();
      const nickRect = nickCell?.getBoundingClientRect();
      const uidClipped = uidRect.right < wrapRect.left + 4;
      const nickClipped = nickRect ? nickRect.right < wrapRect.left + 4 : false;
      if (!uidClipped && !nickClipped) continue;

      const uid = row.getAttribute("data-admin-uid") || "";
      nextPeeks.push({
        key: uid || `row-${nextPeeks.length}`,
        top: rowRect.top - hostRect.top + rowRect.height / 2,
        uid,
        uid2: row.getAttribute("data-admin-uid-2") || "",
        nickname: row.getAttribute("data-admin-nickname") || "",
      });
    }

    setRowIdentityPeeks(nextPeeks);
  }, []);

  useEffect(() => {
    const scrollEl = userTableScrollRef.current;
    if (!scrollEl) return undefined;
    const onScrollOrResize = () => updateRowIdentityPeeks();
    scrollEl.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    onScrollOrResize();
    return () => {
      scrollEl.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize);
    };
  }, [updateRowIdentityPeeks, orderedUsers.length, searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const isReadOnly = admin_possible === "1" || admin_possible === 1;

  return (
    <main className="admin-dashboard">
      <div className="admin-dashboard__glare" aria-hidden="true" />
      <section className="admin-dashboard__panel">
        <header className="admin-dashboard__hero">
          <div>
            <p className="admin-dashboard__eyebrow">Bitgenstone control center</p>
            <h1 className="admin-dashboard__title">Super admin console</h1>
          </div>

          <div className="admin-dashboard__hero-actions">

            {/* <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--ghost"
              onClick={handleMoveBinance}
            >
              Binance 관리자
            </button> */}
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--ghost"
              onClick={() => {
                window.location.href = "/AdminWz";
              }}
            >
              Wizard 관리
            </button>
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--ghost"
              onClick={() => {
                window.location.href = "/AdminHold";
              }}
            >
              Hold 관리
            </button>
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--ghost"
              onClick={() => {
                window.location.href = "/AdminBand";
              }}
            >
              Band 관리
            </button>
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--danger"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </header>

        <div className="admin-dashboard__controls">
          <div className="admin-dashboard__search">
            <input
              type="text"
              placeholder="UID 또는 닉네임으로 검색"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <span>{orderedUsers.length} users</span>
          </div>
        </div>

        <div className="admin-dashboard__table-host" ref={userTableHostRef}>
          {rowIdentityPeeks.map((peek) => (
            <div
              key={peek.key}
              className="admin-dashboard__identity-peek admin-dashboard__identity-peek--row"
              style={{ top: `${peek.top}px` }}
              role="status"
            >
              <strong className="admin-dashboard__identity-peek-nick">
                {peek.nickname || "—"}
              </strong>
              <span className="admin-dashboard__identity-peek-uid">{peek.uid}</span>
              {peek.uid2 ? (
                <span className="admin-dashboard__identity-peek-uid2">2nd {peek.uid2}</span>
              ) : null}
            </div>
          ))}
          <div
            className="admin-dashboard__table-wrapper admin-dashboard__table-wrapper--user-list"
            ref={userTableScrollRef}
          >
          <table className="admin-dashboard__table admin-dashboard__table--compact admin-dashboard__table--user-list">
            <colgroup>
              <col className="admin-dashboard__col-index" />
              <col className="admin-dashboard__col-uid" />
              <col className="admin-dashboard__col-uid2" />
              <col className="admin-dashboard__col-nick" />
              <col className="admin-dashboard__col-status" />
              <col className="admin-dashboard__col-futures" />
              <col className="admin-dashboard__col-futures" />
              <col className="admin-dashboard__col-rate" />
              <col className="admin-dashboard__col-account" />
              <col className="admin-dashboard__col-program" />
              <col className="admin-dashboard__col-info" />
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th>UID</th>
                <th>2nd UID</th>
                <th>Nickname</th>
                <th>승인 상태</th>
                <th>선물잔고</th>
                <th>현재선물잔고</th>
                <th>수익률</th>
                <th>계정</th>
                <th>프로그램 관리</th>
                <th>정보 변경</th>
              </tr>
            </thead>
            <tbody>
              {orderedUsers.length === 0 && (
                <tr>
                  <td colSpan={11}>
                    <div className="admin-dashboard__empty">
                      검색 결과가 없습니다.
                    </div>
                  </td>
                </tr>
              )}
              {orderedUsers.map((user, index) => {
                const returnRate = getReturnRateValue(user);
                const grantedTags = getGrantedFeatureTags(user);
                const selectedFeature = getSelectedFeature(user);

                return (
                  <tr
                    key={user.uid}
                    data-admin-uid={user.uid}
                    data-admin-uid-2={user.uid_2 || ""}
                    data-admin-nickname={user.nickname || ""}
                  >
                    <td>{index + 1}</td>
                    <td className="admin-dashboard__cell-uid">{user.uid}</td>
                    <td className="admin-dashboard__cell-uid">{user.uid_2 || "-"}</td>
                    <td className="admin-dashboard__cell-nick">{user.nickname}</td>
                    <td>
                      <div className="admin-dashboard__status-stack">
                        <span
                          className={
                            user.login_possible === "1" || user.login_possible === 1
                              ? "admin-dashboard__chip admin-dashboard__chip--approved"
                              : "admin-dashboard__chip admin-dashboard__chip--pending"
                          }
                        >
                          {getApprovalLabel(user)}
                        </span>
                        <div className="admin-dashboard__chip-list">
                          {grantedTags.length > 0 ? (
                            grantedTags.map((tag) => (
                              <span
                                key={`${user.uid}-${tag}`}
                                className="admin-dashboard__chip admin-dashboard__chip--feature"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="admin-dashboard__chip admin-dashboard__chip--muted">
                              권한없음
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="admin-dashboard__cell--balance">
                      <div className="admin-dashboard__button-group admin-dashboard__button-group--stacked">
                        <div className="admin-dashboard__button-group admin-dashboard__button-group--futures-row">
                          <button
                            type="button"
                            className="admin-dashboard__button admin-dashboard__button--ghost"
                            onClick={() => handleFetchFuturesBalance(user.uid)}
                            disabled={balanceLoadingByUid[user.uid] || balanceSaveLoadingByUid[user.uid]}
                          >
                            {balanceLoadingByUid[user.uid] || balanceSaveLoadingByUid[user.uid]
                              ? "처리 중..."
                              : "잔고 조회/저장"}
                          </button>
                          <button
                            type="button"
                            className="admin-dashboard__button admin-dashboard__button--approve admin-dashboard__button--solid"
                            onClick={() => openManualBalanceModal(user)}
                            disabled={balanceLoadingByUid[user.uid] || balanceSaveLoadingByUid[user.uid]}
                          >
                            {balanceSaveLoadingByUid[user.uid] ? "처리 중..." : "수동 저장"}
                          </button>
                        </div>
                        {balanceByUid[user.uid] ? (
                          <>
                            <button
                              type="button"
                              className="admin-dashboard__balance"
                              onClick={() => toggleBalanceDetails(user.uid)}
                            >
                              {user.pass_usdt !== null && user.pass_usdt !== undefined && user.pass_usdt !== ""
                                ? `${formatBalance(user.pass_usdt)} USDT`
                                : `${formatBalance(balanceByUid[user.uid].totalBalance)} USDT`}
                            </button>
                            {expandedBalanceByUid[user.uid] ? (
                              <div className="admin-dashboard__balance-details">
                                {balanceByUid[user.uid].usePassUsdtOnly ? (
                                  <>
                                    <p className="admin-dashboard__balance-pass-note">
                                      {
                                        "메인·보조 저장값 없이 PASS USDT 합산만 있는 상태입니다. 상세 구분은「잔고 조회/저장」을 실행한 뒤 확인할 수 있습니다."
                                      }
                                    </p>
                                    <div className="admin-dashboard__balance-line">
                                      <span>PASS 합산</span>
                                      <strong>
                                        {formatBalance(balanceByUid[user.uid].totalBalance)} USDT
                                      </strong>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="admin-dashboard__balance-line">
                                      <span>메인계정</span>
                                      <strong>
                                        {formatBalance(balanceByUid[user.uid].mainBalance)} USDT
                                      </strong>
                                    </div>
                                    <div className="admin-dashboard__balance-line">
                                      <span>보조계정</span>
                                      <strong>
                                        {formatBalance(balanceByUid[user.uid].secondBalance)} USDT
                                      </strong>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span
                            className={
                              user.pass_usdt !== null &&
                              user.pass_usdt !== undefined &&
                              user.pass_usdt !== ""
                                ? "admin-dashboard__balance-display"
                                : "admin-dashboard__balance-display admin-dashboard__balance-display--placeholder"
                            }
                          >
                            {user.pass_usdt !== null && user.pass_usdt !== undefined && user.pass_usdt !== ""
                              ? `${formatBalance(user.pass_usdt)} USDT`
                              : "-"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="admin-dashboard__cell--balance">
                      <div className="admin-dashboard__button-group admin-dashboard__button-group--stacked">
                        <button
                          type="button"
                          className="admin-dashboard__button admin-dashboard__button--ghost"
                          onClick={() => handleFetchCurrentFuturesBalance(user.uid)}
                          disabled={currentBalanceLoadingByUid[user.uid]}
                        >
                          {currentBalanceLoadingByUid[user.uid] ? "처리 중..." : "현재 잔고 재조회"}
                        </button>
                        {currentBalanceByUid[user.uid] &&
                        user.current_usdt !== null &&
                        user.current_usdt !== undefined &&
                        user.current_usdt !== "" ? (
                          <>
                            <div className="admin-dashboard__balance admin-dashboard__balance--current">
                              {`${formatBalance(user.current_usdt)} USDT`}
                            </div>
                            {currentBalanceByUid[user.uid].mainBalance !== null &&
                              currentBalanceByUid[user.uid].mainBalance !== undefined &&
                              currentBalanceByUid[user.uid].secondBalance !== null &&
                              currentBalanceByUid[user.uid].secondBalance !== undefined && (
                                <div className="admin-dashboard__balance-details">
                                  <div className="admin-dashboard__balance-card">
                                    <div className="admin-dashboard__balance-card-title">메인계정</div>
                                    <div className="admin-dashboard__balance-line">
                                      <span>Wallet</span>
                                      <strong>
                                        {formatBalance(currentBalanceByUid[user.uid].mainBalance)} USDT
                                      </strong>
                                    </div>
                                  </div>
                                  <div className="admin-dashboard__balance-card">
                                    <div className="admin-dashboard__balance-card-title">보조계정</div>
                                    <div className="admin-dashboard__balance-line">
                                      <span>Wallet</span>
                                      <strong>
                                        {formatBalance(currentBalanceByUid[user.uid].secondBalance)} USDT
                                      </strong>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </>
                        ) : (
                          <span
                            className={
                              user.current_usdt !== null &&
                              user.current_usdt !== undefined &&
                              user.current_usdt !== ""
                                ? "admin-dashboard__balance-display"
                                : "admin-dashboard__balance-display admin-dashboard__balance-display--placeholder"
                            }
                          >
                            {user.current_usdt !== null && user.current_usdt !== undefined && user.current_usdt !== ""
                              ? `${formatBalance(user.current_usdt)} USDT`
                              : "-"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span
                        className={
                          returnRate === null
                            ? "admin-dashboard__rate admin-dashboard__rate--neutral"
                            : returnRate > 0
                              ? "admin-dashboard__rate admin-dashboard__rate--positive"
                              : returnRate < 0
                                ? "admin-dashboard__rate admin-dashboard__rate--negative"
                                : "admin-dashboard__rate admin-dashboard__rate--neutral"
                        }
                      >
                        {formatRate(returnRate)}
                      </span>
                    </td>
                    <td>
                      <div className="admin-dashboard__button-group admin-dashboard__button-group--stacked">
                        <select
                          className="admin-dashboard__select"
                          value={getSelectedAccountAction(user.uid)}
                          onChange={handleAccountActionChange(user.uid)}
                        >
                          <option value="approve">승인</option>
                          <option value="revoke">삭제</option>
                        </select>
                        <button
                          type="button"
                          className="admin-dashboard__button admin-dashboard__button--approve admin-dashboard__button--solid"
                          disabled={isReadOnly && getSelectedAccountAction(user.uid) === "revoke"}
                          onClick={() => executeAccountAction(user)}
                        >
                          실행
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="admin-dashboard__button-group admin-dashboard__button-group--stacked admin-dashboard__program-cell">
                        <select
                          className="admin-dashboard__select"
                          value={selectedFeature.key}
                          onChange={handleSelectedFeatureChange(user.uid)}
                        >
                          {featureConfigs.map((feature) => (
                            <option key={feature.key} value={feature.key}>
                              {feature.label}
                            </option>
                          ))}
                        </select>
                        <div className="admin-dashboard__program-actions">
                          <div className="admin-dashboard__button-group admin-dashboard__button-group--split-pair">
                            <button
                              type="button"
                              className="admin-dashboard__button admin-dashboard__button--approve admin-dashboard__button--solid"
                              disabled={isReadOnly}
                              onClick={() =>
                                selectedFeature.approveHandler(user.uid, user.nickname)
                              }
                            >
                              승인
                            </button>
                            <button
                              type="button"
                              className="admin-dashboard__button admin-dashboard__button--release"
                              disabled={isReadOnly}
                              onClick={() =>
                                selectedFeature.revokeHandler(user.uid, user.nickname)
                              }
                            >
                              해제
                            </button>
                          </div>
                          <button
                            type="button"
                            className="admin-dashboard__button admin-dashboard__button--approve admin-dashboard__button--solid admin-dashboard__button--full"
                            onClick={() => handleFeatureConnect(selectedFeature, user.uid, user.nickname)}
                            disabled={transferLoadingByUid[user.uid]}
                          >
                            {transferLoadingByUid[user.uid] ? "처리 중..." : "연결"}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="admin-dashboard__button-group admin-dashboard__button-group--stacked">
                        <select
                          className="admin-dashboard__select"
                          value={getSelectedInfoAction(user)}
                          onChange={handleInfoActionChange(user.uid)}
                        >
                          <option value="nickname">닉네임</option>
                          <option value="email">이메일</option>
                          <option value="password">비밀번호</option>
                          <option value="main_api">메인 API</option>
                          <option value="second_api" disabled={!user.uid_2}>
                            보조 API
                          </option>
                        </select>
                        <button
                          type="button"
                          className="admin-dashboard__button admin-dashboard__button--ghost"
                          disabled={
                            isReadOnly ||
                            apiUpdateLoadingByUid[user.uid] ||
                            (getSelectedInfoAction(user) === "second_api" && !user.uid_2)
                          }
                          onClick={() => executeInfoAction(user)}
                        >
                          {apiUpdateLoadingByUid[user.uid] ? "처리 중..." : "변경"}
                        </button>
                        <button
                          type="button"
                          className="admin-dashboard__button admin-dashboard__button--ghost"
                          disabled={futuresSpotLoadingByUid[user.uid]}
                          onClick={() => openFuturesSpotTransferModal(user)}
                        >
                          {futuresSpotLoadingByUid[user.uid] ? "처리 중..." : "Transfer"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </section>


      {manualBalanceModal.isOpen && (
        <div
          className="admin-dashboard__modal-backdrop"
          onClick={closeManualBalanceModal}
          role="presentation"
        >
          <div
            className="admin-dashboard__modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="manual-balance-modal-title"
          >
            <div className="admin-dashboard__modal-header">
              <div>
                <p className="admin-dashboard__modal-eyebrow">Manual Futures Save</p>
                <h3 id="manual-balance-modal-title">수동 잔고 저장</h3>
              </div>
              <button
                type="button"
                className="admin-dashboard__modal-close"
                onClick={closeManualBalanceModal}
              >
                x
              </button>
            </div>

            <div className="admin-dashboard__modal-summary">
              <span>닉네임: {manualBalanceModal.nickname || "-"}</span>
              <span>UID: {manualBalanceModal.uid || "-"}</span>
              <span>보조 UID: {manualBalanceModal.uid2 || "-"}</span>
            </div>

            <div className="admin-dashboard__modal-body">
              <label className="admin-dashboard__modal-field">
                <span>메인계정 잔고</span>
                <input
                  type="text"
                  value={manualBalanceModal.mainBalance}
                  onChange={handleManualBalanceFieldChange("mainBalance")}
                  placeholder="예: 1234.567"
                />
              </label>

              <label className="admin-dashboard__modal-field">
                <span>보조계정 잔고</span>
                <input
                  type="text"
                  value={manualBalanceModal.secondBalance}
                  onChange={handleManualBalanceFieldChange("secondBalance")}
                  placeholder="예: 234.567"
                />
              </label>
            </div>

            <div className="admin-dashboard__modal-actions">
              <button
                type="button"
                className="admin-dashboard__modal-cancel"
                onClick={closeManualBalanceModal}
                disabled={balanceSaveLoadingByUid[manualBalanceModal.uid]}
              >
                취소
              </button>
              <button
                type="button"
                className="admin-dashboard__modal-save"
                onClick={handleManualSaveBalance}
                disabled={balanceSaveLoadingByUid[manualBalanceModal.uid]}
              >
                {balanceSaveLoadingByUid[manualBalanceModal.uid] ? "저장 중..." : "저장"}
      </button>
            </div>
          </div>
        </div>
      )}
      {apiChangeModal.isOpen && (
        <div
          className="admin-dashboard__modal-backdrop"
          onClick={closeApiChangeModal}
          role="presentation"
        >
          <div
            className="admin-dashboard__modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="api-change-modal-title"
          >
            <div className="admin-dashboard__modal-header">
              <div>
                <p className="admin-dashboard__modal-eyebrow">API Key Update</p>
                <h3 id="api-change-modal-title">
                  {apiChangeModal.type === "main" ? "메인 API 변경" : "보조 API 변경"}
                </h3>
              </div>
              <button
                type="button"
                className="admin-dashboard__modal-close"
                onClick={closeApiChangeModal}
              >
                x
              </button>
            </div>

            <div className="admin-dashboard__modal-summary">
              <span>닉네임: {apiChangeModal.nickname || "-"}</span>
              <span>UID: {apiChangeModal.uid || "-"}</span>
            </div>

            <div className="admin-dashboard__modal-body">
              <label className="admin-dashboard__modal-field">
                <span>API Key</span>
                <input
                  type="text"
                  value={apiChangeModal.api}
                  onChange={handleApiChangeField("api")}
                  placeholder="새 API Key 입력"
                />
              </label>

              <label className="admin-dashboard__modal-field">
                <span>SecretKey</span>
                <input
                  type="text"
                  value={apiChangeModal.secretkey}
                  onChange={handleApiChangeField("secretkey")}
                  placeholder="새 SecretKey 입력"
                />
              </label>

              <label className="admin-dashboard__modal-field">
                <span>API PW</span>
                <input
                  type="text"
                  value={apiChangeModal.apiPw}
                  onChange={handleApiChangeField("apiPw")}
                  placeholder="새 API PW 입력"
                />
              </label>
            </div>

            <div className="admin-dashboard__modal-actions">
              <button
                type="button"
                className="admin-dashboard__modal-cancel"
                onClick={closeApiChangeModal}
                disabled={apiUpdateLoadingByUid[apiChangeModal.uid]}
              >
                취소
              </button>
              <button
                type="button"
                className="admin-dashboard__modal-save"
                onClick={handleSaveApiChange}
                disabled={apiUpdateLoadingByUid[apiChangeModal.uid]}
              >
                {apiUpdateLoadingByUid[apiChangeModal.uid] ? "검증 중..." : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}
      {futuresSpotTransferModal.isOpen && (
        <div
          className="admin-dashboard__modal-backdrop"
          onClick={closeFuturesSpotTransferModal}
          role="presentation"
        >
          <div
            className="admin-dashboard__modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="futures-spot-transfer-modal-title"
          >
            <div className="admin-dashboard__modal-header">
              <div>
                <p className="admin-dashboard__modal-eyebrow">Futures ↔ Spot Transfer</p>
                <h3 id="futures-spot-transfer-modal-title">선물 ↔ 현물 USDT 이동</h3>
              </div>
              <button
                type="button"
                className="admin-dashboard__modal-close"
                onClick={closeFuturesSpotTransferModal}
              >
                x
              </button>
            </div>

            <div className="admin-dashboard__modal-summary">
              <span>닉네임: {futuresSpotTransferModal.nickname || "-"}</span>
              <span>메인 UID: {futuresSpotTransferModal.uid || "-"}</span>
              <span>보조 UID: {futuresSpotTransferModal.uid2 || "-"}</span>
            </div>

            <div className="admin-dashboard__modal-body">
              <div className="admin-dashboard__modal-field">
                <span>계정 선택</span>
                <div className="admin-dashboard__transfer-toggle">
                  <button
                    type="button"
                    className={`admin-dashboard__transfer-toggle-btn${futuresSpotTransferModal.account === "main" ? " admin-dashboard__transfer-toggle-btn--active" : ""}`}
                    onClick={() => handleFuturesSpotTransferFieldChange("account")({ target: { value: "main" } })}
                    disabled={futuresSpotLoadingByUid[futuresSpotTransferModal.uid]}
                  >
                    <span className="admin-dashboard__transfer-toggle-icon">👤</span>
                    <span>메인계정</span>
                    <span className="admin-dashboard__transfer-toggle-sub">{futuresSpotTransferModal.uid || "-"}</span>
                  </button>
                  <button
                    type="button"
                    className={`admin-dashboard__transfer-toggle-btn${futuresSpotTransferModal.account === "second" ? " admin-dashboard__transfer-toggle-btn--active" : ""}`}
                    onClick={() => handleFuturesSpotTransferFieldChange("account")({ target: { value: "second" } })}
                    disabled={futuresSpotLoadingByUid[futuresSpotTransferModal.uid] || !futuresSpotTransferModal.uid2}
                  >
                    <span className="admin-dashboard__transfer-toggle-icon">👥</span>
                    <span>보조계정</span>
                    <span className="admin-dashboard__transfer-toggle-sub">{futuresSpotTransferModal.uid2 || "없음"}</span>
                  </button>
                </div>
              </div>

              {futuresSpotTransferModal.balanceLoading ? (
                <div className="admin-dashboard__modal-field" style={{ textAlign: "center", color: "#888", padding: "6px 0" }}>
                  잔고 조회 중...
                </div>
              ) : futuresSpotTransferModal.balanceInfo &&
                futuresSpotTransferModal.balanceInfo.forAccount === futuresSpotTransferModal.account ? (
                <div className="admin-dashboard__balance-details" style={{ margin: "4px 0 8px" }}>
                  <div style={{ fontSize: "0.8em", fontWeight: "bold", color: "#aaa", marginBottom: "4px" }}>
                    {futuresSpotTransferModal.account === "main" ? "메인계정" : "보조계정"} 잔고
                  </div>
                  <div className="admin-dashboard__balance-line">
                    <span>📤 선물 잔고</span>
                    <span>{futuresSpotTransferModal.balanceInfo.futures} USDT</span>
                  </div>
                  <div className="admin-dashboard__balance-line">
                    <span>💰 현물 잔고</span>
                    <span>{futuresSpotTransferModal.balanceInfo.spot} USDT</span>
                  </div>
                </div>
              ) : null}

              <div className="admin-dashboard__modal-field">
                <span>이동 방향</span>
                <div className="admin-dashboard__transfer-toggle">
                  <button
                    type="button"
                    className={`admin-dashboard__transfer-toggle-btn${futuresSpotTransferModal.direction === "futures_to_spot" ? " admin-dashboard__transfer-toggle-btn--active" : ""}`}
                    onClick={() => handleFuturesSpotTransferFieldChange("direction")({ target: { value: "futures_to_spot" } })}
                    disabled={futuresSpotLoadingByUid[futuresSpotTransferModal.uid]}
                  >
                    <span className="admin-dashboard__transfer-toggle-icon">📤</span>
                    <span>선물 → 현물</span>
                  </button>
                  <button
                    type="button"
                    className={`admin-dashboard__transfer-toggle-btn${futuresSpotTransferModal.direction === "spot_to_futures" ? " admin-dashboard__transfer-toggle-btn--active-warn" : ""}`}
                    onClick={() => handleFuturesSpotTransferFieldChange("direction")({ target: { value: "spot_to_futures" } })}
                    disabled={futuresSpotLoadingByUid[futuresSpotTransferModal.uid]}
                  >
                    <span className="admin-dashboard__transfer-toggle-icon">📥</span>
                    <span>현물 → 선물</span>
                  </button>
                </div>
              </div>

              <label className="admin-dashboard__modal-field">
                <span>이동 USDT 수량</span>
                <input
                  type="text"
                  value={futuresSpotTransferModal.amount}
                  onChange={handleFuturesSpotTransferFieldChange("amount")}
                  placeholder="예: 100"
                  disabled={futuresSpotLoadingByUid[futuresSpotTransferModal.uid]}
                />
              </label>
            </div>

            <div className="admin-dashboard__modal-actions">
              <button
                type="button"
                className="admin-dashboard__modal-cancel"
                onClick={closeFuturesSpotTransferModal}
                disabled={futuresSpotLoadingByUid[futuresSpotTransferModal.uid]}
              >
                취소
              </button>
              <button
                type="button"
                className="admin-dashboard__modal-save"
                onClick={handleSubmitFuturesSpotTransfer}
                disabled={futuresSpotLoadingByUid[futuresSpotTransferModal.uid]}
              >
                {futuresSpotLoadingByUid[futuresSpotTransferModal.uid] ? "이동 중..." : "이동 실행"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
